import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MailCheck, ArrowLeft } from "lucide-react";
import { Button, Card, Input, Select } from "@nutrayan/ui";
import { emailSchema } from "@nutrayan/utils";
import {
  createAdmin,
  CREATABLE_ROLES,
  CREATABLE_LEVELS,
  type AdminCapabilityRole,
} from "@/lib/staff-api";
import { useClaims } from "@/lib/use-claims";
import { tenantLabel } from "@/lib/permissions";

const schema = z.object({
  fullName: z.string().trim().min(2, "Enter the administrator's name").max(120),
  email: emailSchema,
  role: z.enum(["ADMIN_SUPER", "ADMIN_OPS", "ADMIN_MARKETING", "ADMIN_VIEWER"]),
  adminLevel: z.enum(["SUB", "TENANT_SUPER"]),
  tenantId: z.string().trim().min(1, "Tenant is required"),
});
type Form = z.infer<typeof schema>;

export default function CreateAdminPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { tenantId } = useClaims();
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);

  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: "",
      email: "",
      role: "ADMIN_OPS",
      adminLevel: "SUB",
      tenantId: tenantId ?? "",
    },
  });

  const create = useMutation({
    mutationFn: (data: Form) =>
      createAdmin({
        fullName: data.fullName.trim(),
        email: data.email.trim(),
        role: data.role as AdminCapabilityRole,
        adminLevel: data.adminLevel,
        tenantId: data.tenantId.trim(),
      }),
    onSuccess: (_admin, data) => {
      qc.invalidateQueries({ queryKey: ["admin", "staff"] });
      setInvitedEmail(data.email.trim());
    },
  });

  if (invitedEmail) {
    return (
      <div className="mx-auto max-w-lg py-12">
        <Card className="text-center">
          <div className="bg-forest/10 text-forest mx-auto flex h-14 w-14 items-center justify-center rounded-full">
            <MailCheck size={26} aria-hidden />
          </div>
          <h1 className="font-display text-display-md text-charcoal mt-4">Invitation sent</h1>
          <p className="text-body-sm text-ink mt-2">
            Invitation sent successfully. The administrator will receive an email to securely create
            their own password. No password was set by you.
          </p>
          <p className="text-caption text-muted mt-1">{invitedEmail}</p>
          <div className="mt-6 flex flex-col gap-2">
            <Button
              size="lg"
              fullWidth
              onClick={() => {
                setInvitedEmail(null);
                form.reset({ ...form.getValues(), fullName: "", email: "" });
              }}
            >
              Invite another admin
            </Button>
            <Button size="md" variant="ghost" fullWidth onClick={() => navigate("/staff")}>
              Back to Staff Registry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg py-6">
      <Link to="/staff" className="text-caption text-forest mb-4 inline-flex items-center gap-1">
        <ArrowLeft size={13} aria-hidden /> Staff Registry
      </Link>
      <Card>
        <h1 className="font-display text-display-md text-charcoal">Create administrator</h1>
        <p className="text-body-sm text-ink mt-1">
          You never set a password. The new admin receives a secure email to create their own.
        </p>

        <form className="mt-6 space-y-4" onSubmit={form.handleSubmit((d) => create.mutate(d))}>
          <div>
            <label className="text-body-sm text-ink" htmlFor="fullName">
              Full name
            </label>
            <Input
              id="fullName"
              {...form.register("fullName")}
              invalid={!!form.formState.errors.fullName}
            />
            {form.formState.errors.fullName ? (
              <p className="text-caption text-terracotta mt-1">
                {form.formState.errors.fullName.message}
              </p>
            ) : null}
          </div>

          <div>
            <label className="text-body-sm text-ink" htmlFor="email">
              Email
            </label>
            <Input
              id="email"
              type="email"
              {...form.register("email")}
              invalid={!!form.formState.errors.email}
            />
            {form.formState.errors.email ? (
              <p className="text-caption text-terracotta mt-1">
                {form.formState.errors.email.message}
              </p>
            ) : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-body-sm text-ink" htmlFor="role">
                Capability role
              </label>
              <Select id="role" {...form.register("role")}>
                {CREATABLE_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="text-body-sm text-ink" htmlFor="adminLevel">
                Admin level
              </label>
              <Select id="adminLevel" {...form.register("adminLevel")}>
                {CREATABLE_LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <label className="text-body-sm text-ink" htmlFor="tenantId">
              Tenant
            </label>
            <Input
              id="tenantId"
              {...form.register("tenantId")}
              invalid={!!form.formState.errors.tenantId}
            />
            <p className="text-caption text-muted mt-1">
              Defaults to your tenant ({tenantLabel(tenantId)}). Tenant isolation is enforced by the
              backend.
            </p>
          </div>

          {create.isError ? (
            <p className="text-caption text-terracotta">
              {create.error instanceof Error && /email/i.test(create.error.message)
                ? "That email is already registered to an administrator."
                : "Couldn't create the administrator. Please check the details and try again."}
            </p>
          ) : null}

          <Button size="lg" fullWidth type="submit" disabled={create.isPending}>
            {create.isPending ? "Sending invitation…" : "Send invitation"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
